import {
  el,
  type BlockSignal,
  type BlockSpec,
  type CtaValue,
  type IRNode,
  type Issue,
} from '@lp/shared';
import { isValidAnalyticsId, type ButtonRadius, type ButtonSize } from '../shared/ctaEmbed.js';
import { buttonNode, buttonStyleDefaults, buttonStyleFields, buttonStyleFrom } from '../shared/button.js';
import { compact, heading, paragraph, section, stack } from '../shared/helpers.js';
import { ctaGroupCls } from '../shared/patterns.js';

export interface CtaBandProps extends Record<string, unknown> {
  headline: string;
  sub: string;
  cta: CtaValue;
  buttonSize: ButtonSize;
  buttonRadius: ButtonRadius;
  buttonFullWidth: boolean;
  note: string;
  tone: 'indigo' | 'pink' | 'surface';
}

export const CtaBand: BlockSpec<CtaBandProps> = {
  id: 'CtaBand',
  label: 'CTA band',
  category: 'both',
  description: 'Full-width call to action. One per page, near the end.',

  fields: {
    headline: { type: 'text', label: 'Headline (H2)', maxChars: 70, lint: 'heading' },
    sub: { type: 'textarea', label: 'Supporting copy', maxChars: 180, lint: 'body' },
    cta: { type: 'cta', label: 'Call to action' },
    ...buttonStyleFields('button'),
    note: { type: 'text', label: 'Reassurance line', maxChars: 80, lint: 'body' },
    tone: {
      type: 'radio',
      label: 'Background',
      help: 'Pink is the loudest option. The brand rule is that it appears once per page.',
      options: [
        { label: 'Indigo', value: 'indigo' },
        { label: 'Pink', value: 'pink' },
        { label: 'Soft grey', value: 'surface' },
      ],
    },
  } as BlockSpec<CtaBandProps>['fields'],

  defaultProps: {
    headline: 'Start collecting in under a minute',
    sub: 'Create a pot, share the link, and let everyone pay however suits them.',
    cta: {
      label: 'Collect money now',
      href: 'https://app.collctiv.com/signup',
      analyticsId: 'lpFooterCTA',
      location: 'website',
      utmContent: 'lp_footer',
    },
    ...buttonStyleDefaults('button'),
    // The closing CTA is the loudest moment on the page, so it starts large.
    buttonSize: 'large',
    note: '',
    tone: 'indigo',
  } as CtaBandProps,

  toIR(props): IRNode {
    const { tone } = props;

    const inner = stack(
      compact([
        heading(2, props.headline, tone, true),
        paragraph('lp-lead', props.sub, tone, true),
        el(
          'Block',
          'div',
          ctaGroupCls('center'),
          [buttonNode(props.cta, 'primary', buttonStyleFrom(props, 'button'))],
          { displayName: 'CTA group' },
        ),
        paragraph('lp-cta-note', props.note, tone, true),
      ]),
      { centred: true, displayName: 'CTA copy' },
    );

    return section(tone, [inner], { narrow: true, displayName: 'CTA band' });
  },

  signals(props): BlockSignal[] {
    return [
      { kind: 'heading', level: 2, text: props.headline },
      { kind: 'text', fieldPath: 'headline', value: props.headline, scope: 'heading' },
      { kind: 'text', fieldPath: 'sub', value: props.sub, scope: 'body' },
      { kind: 'cta', analyticsId: props.cta.analyticsId, tone: props.tone },
    ];
  },

  validate(props): Issue[] {
    const issues: Issue[] = [];
    if (!isValidAnalyticsId(props.cta.analyticsId)) {
      issues.push({
        id: 'cta-analytics-id',
        severity: 'error',
        message:
          'The CTA needs a valid analytics id — it becomes the button’s DOM id and the ' +
          'position reported to PostHog.',
        location: { fieldPath: 'cta' },
      });
    }
    if (!props.headline.trim()) {
      issues.push({
        id: 'cta-no-headline',
        severity: 'warn',
        message: 'A CTA band with no headline gives the reader no reason to act.',
        location: { fieldPath: 'headline' },
      });
    }
    return issues;
  },
};
